import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { AlertColor, Link } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { authAPI } from '../../shared/api/AuthAPI'
import { useAppSelector } from '../../store/hooks'
import FsAuthWrapper from './auth-wrapper'
import { validateEmail } from '../../shared/utils'
import { ReactComponent as SuccessTickLogo } from '../../assets/success_tick.svg'
import LoadingDash from '../common/atoms/LoadingDash'

const FsForgotPassword = () => {
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState<string | null>(null)
  const [severity, setSeverity] = useState<AlertColor>('error')
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const [retryIn, setRetryIn] = useState<number>(30)
  const navigate = useNavigate()

  let timer: any = null

  const countdown = (r: number) => {
    if (r === 0) {
      clearTimeout(timer)
    } else {
      setRetryIn(r - 1)
      clearTimeout(timer)
      timer = setInterval(() => countdown(r - 1), 1000)
    }
  }

  const triggerPasswordResetEmail = async () => {
    try {
      setLoading(true)
      if (!email) {
        setSeverity('error')
        setMessage('enter.valid.email')
        return
      }
      await authAPI.triggerPasswordReset(email.toLowerCase())
      setSeverity('success')
      setMessage('password.link.sent')
      setRetryIn(30)
      timer = setInterval(() => countdown(30), 1000)
    } catch (e: any) {
      setSeverity('error')
      if (e.response.status === 404) {
        setMessage('invalid.email')
      } else {
        setMessage('something.went.wrong')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault()
        if (!loading && email && validateEmail(email ?? '')) triggerPasswordResetEmail()
      }
    }
    document.addEventListener('keydown', listener)
    return () => {
      document.removeEventListener('keydown', listener)
    }
  }, [email])

  return (
    <FsAuthWrapper>
      {message && severity === 'success' ? (
        <>
          <Typography variant="h1" color="primary" mb={4} textAlign="center">
            {t('forgot.pwd.success')}
          </Typography>
          <SuccessTickLogo />
          <Typography mt={4} textAlign="center">
            <Trans>{t('forgot.pwd.success.desc', { email })}</Trans>
          </Typography>
          <Button
            fullWidth
            className="underline hover:underline mt-3"
            onClick={() => navigate('/login')}
          >
            {t('go.to.login')}
          </Button>

          <Button
            variant="outlined"
            sx={{ mt: 6, mb: 1, width: 350 }}
            onClick={triggerPasswordResetEmail}
            disabled={retryIn !== 0}
          >
            {retryIn !== 0 ? t('forgot.pwd.retry', { seconds: retryIn }) : t('forgot.pwd.again')}
          </Button>
          <Typography textAlign="center" variant="body2" mt={1}>
            {`${t('forgot.pwd.success.issue')} `}
            <Link href="mailto:support@ines-analytics.com">
              {currentLanguage === 'de' ? 'Kontaktieren Sie uns' : 'Contact us'}
            </Link>
          </Typography>
        </>
      ) : (
        <Box
          sx={{
            marginY: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '80%',
            maxHeight: '450px',
            overflowY: 'scroll',
          }}
        >
          {message && (
            <Typography color="error" textAlign="center" mt={1} mb={2}>
              {`${t(message as any)} `}
              <Link href="mailto:support@ines-analytics.com" color="error">
                {currentLanguage === 'de' ? 'kontaktieren Sie uns' : 'contact us'}
              </Link>
            </Typography>
          )}
          <Typography variant="h1" color="primary">
            {t('forgot.pwd.title')}
          </Typography>
          <Typography textAlign="center" my={2}>
            {t('forgot.pwd.desc')}
          </Typography>
          <Box sx={{ width: 350, maxWidth: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              size="small"
              id="email"
              label={t('email.address')}
              name="email"
              autoComplete="email"
              autoFocus
              error={email !== null && !validateEmail(email)}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            {email !== null && !validateEmail(email) && (
              <Typography ml={1} color="error" variant="body2">
                {t('email.error')}
              </Typography>
            )}
            <Button
              type="submit"
              disabled={loading || !validateEmail(email ?? '')}
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              onClick={triggerPasswordResetEmail}
            >
              {t('forgot.pwd.title')}
            </Button>
            <Button
              fullWidth
              className="underline hover:underline mt-3"
              onClick={() => navigate('/login')}
            >
              {t('go.to.login')}
            </Button>
          </Box>
        </Box>
      )}
      {loading && (
        <Box sx={{ position: 'absolute' }}>
          <LoadingDash title={t('loading')} />
        </Box>
      )}
    </FsAuthWrapper>
  )
}

export default FsForgotPassword
